import React from "react";
import { useFormikContext } from "formik";
import { getFieldPath } from "utils/form";
import get from "lodash/get";
import {
  useCtxReferences,
  useIsAuthed,
  usePaymentMethodSelections
} from "utils/selectors";
import StripeExternalLink from "components/StripeExternalLink";
import DocsInfoButton from "components/DocsInfoButton";
import DigitalWalletPrompt from "components/DigitalWalletPrompt";
import { LEARN_PATHS } from "utils/paths";
import {
  PAYMENT_METHODS_DOCS_INFO,
  SETUP_PAYMENT_METHODS_DOCS_INFO
} from "constants/docs";
import { styles } from "components/AcceptedPaymentMethods/styles";
import { getStripeDestination } from "utils/stripe";
import { STRIPE_ROUTES } from "utils/constants/stripe";
import { PAYMENT_METHOD_COPY, PAYMENT_MODE } from "utils/constants/payment";
import { STATE_KEYS } from "utils/constants/state";
import { getIconForUID } from "components/FormerEditor/utils";
import { COLORS } from "utils/styles";
import { useShortLinkPaymentMethods } from "utils/hooks/shortLink";
import Toggle from "react-toggle";
import { getToggleProps } from "utils/form";
import { omitDeep } from "utils/data";
import classnames from "classnames";
import PaymentMethodSelect from "./PaymentMethodSelect";
import PaymentMethodTypesSummary from "./PaymentMethodTypesSummary";
import { useMessagesError } from "utils/hooks/editor";

const Icon = getIconForUID("checkmark");

const DEFAULT_TITLE = "Payment methods";

const Methods = (
  { name, hideLabel, inherited, title, hideToggle } = {
    title: DEFAULT_TITLE
  }
) => {
  const ctxTitle = title || DEFAULT_TITLE;
  const {
    values,
    touched,
    errors,
    setValues,
    setFieldValue,
    setFieldTouched
  } = useFormikContext();

  const isAuthed = useIsAuthed();
  const { merchantAccount } = useCtxReferences();
  const account = get(merchantAccount, "account");

  const paymentMethodsPath = getFieldPath(name, "payment_method_types");
  const hasPaymentMethodError = useMessagesError(
    STATE_KEYS.SHORT_LINK.PAYMENT_METHOD_TYPES
  );

  const paymentMethodsValue = get(values, paymentMethodsPath) || [];

  const modePath = getFieldPath(name, "mode");
  const modeValue = get(values, modePath, []);
  const isSetupMode = modeValue === PAYMENT_MODE.SETUP;

  const { options, selections } = usePaymentMethodSelections(
    paymentMethodsValue,
    name
  );

  const {
    inheritedPaymentMethodTypes,
    activeInheritence,
    hasDefinedPaymentMethodTypes
  } = useShortLinkPaymentMethods({ inherited });

  if (activeInheritence) {
    return (
      <PaymentMethodTypesSummary
        title={ctxTitle}
        methods={inheritedPaymentMethodTypes}
      />
    );
  } else {
    const hasSelections = selections.length > 0;

    const autoTaxesEnabled = Boolean(
      get(values, STATE_KEYS.SHORT_LINK.AUTOMATIC_TAX_ENABLED)
    );

    const managePaymentMethodsLink = (
      <div className="w-100 db items-start pt2">
        <StripeExternalLink
          customClasses={{
            link:
              "link flex flex-row items-center link__decoration--hover color-primary pointer w-100 word-break-all",
            copy: "f6 fw6 lh-title pr1"
          }}
          copy="Manage payment methods"
          href={getStripeDestination({
            path: STRIPE_ROUTES.PAYMENT_METHODS,
            test: false
          })}
        />
      </div>
    );
    const authedAccount = Boolean(isAuthed && account);
    const authedWithSelections = authedAccount && hasSelections;
    const authedWithoutSelections = authedAccount && !hasSelections;

    return (
      <div className="flex flex-column w-100">
        <div className="flex flex-row items-center justify-between pb2">
          {!hideLabel && (
            <label
              className={classnames("f5 fw6 db", {
                "color-error": hasPaymentMethodError
              })}
            >
              {ctxTitle}
            </label>
          )}
          <div className="dib">
            <div className="flex flex-row justify-center items-center">
              <div className="pr1">
                <DocsInfoButton
                  slides={
                    isSetupMode
                      ? SETUP_PAYMENT_METHODS_DOCS_INFO
                      : PAYMENT_METHODS_DOCS_INFO
                  }
                  url={LEARN_PATHS.PAYMENT_LINKS.PAYMENT_METHODS_RESTRICTIONS}
                />
              </div>
              {!hideToggle && (
                <Toggle
                  {...getToggleProps({
                    active: hasDefinedPaymentMethodTypes,
                    setFieldValue: (_, active) => {
                      if (
                        !get(
                          touched,
                          STATE_KEYS.SHORT_LINK.PAYMENT_METHOD_TYPES
                        )
                      ) {
                        setFieldTouched(
                          STATE_KEYS.SHORT_LINK.PAYMENT_METHOD_TYPES,
                          true
                        );
                      }

                      setTimeout(() => {
                        if (active) {
                          setFieldValue(
                            STATE_KEYS.SHORT_LINK.PAYMENT_METHOD_TYPES,
                            []
                          );
                        } else {
                          setValues(
                            omitDeep(
                              values,
                              STATE_KEYS.SHORT_LINK.PAYMENT_METHOD_TYPES
                            )
                          );
                        }
                      }, 0);
                    }
                  })}
                />
              )}
            </div>
          </div>
        </div>
        <PaymentMethodSelect
          name={name}
          selections={selections}
          options={options}
        />
        {!hasSelections && (
          <div className="pv3 flex flex-column w-100">
            <div className="flex flex-row justify-between items-center">
              <div className="f6 fw5 db">Automatic methods</div>
              <Icon size={20} color={COLORS.blue} />
            </div>
            <div className="f6 pt2 lh-copy">
              {isSetupMode
                ? PAYMENT_METHOD_COPY.SETUP
                : PAYMENT_METHOD_COPY.AUTOMATIC}
            </div>
            {authedWithoutSelections && managePaymentMethodsLink}
          </div>
        )}
        {/* https://dashboard.stripe.com/settings/payment_methods */}
        {!isSetupMode && <DigitalWalletPrompt enabled={!autoTaxesEnabled} />}
        {authedWithSelections && managePaymentMethodsLink}
        <style jsx>{styles}</style>
      </div>
    );
  }
};

export default Methods;
